import { ChangeEvent, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useRemovePhoto, useUploadPhoto } from "../../application/usePublicProfileSettings";

/** Matches what the API accepts. Checked here too so an obviously wrong file
 *  is refused before we ask for an upload URL. */
const ACCEPTED = ["image/jpeg", "image/png", "image/webp"];
const MAX_BYTES = 5 * 1024 * 1024;

export default function ProfilePhotoUploader({ photoUrl }: { photoUrl: string | null }) {
    const { t } = useTranslation();
    const inputRef = useRef<HTMLInputElement>(null);
    const [error, setError] = useState<string | null>(null);
    // A stored photo whose URL will not load is a different state from having no
    // photo: the therapist believes one is published. Saying so beats rendering
    // a broken image and its alt text, which is what a bucket misconfiguration
    // looked like from here.
    const [unreachable, setUnreachable] = useState(false);
    const upload = useUploadPhoto();
    const remove = useRemovePhoto();

    useEffect(() => setUnreachable(false), [photoUrl]);

    const pick = (event: ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        event.target.value = "";
        if (!file) return;

        if (!ACCEPTED.includes(file.type)) {
            setError(t("publicDirectory.settings.photo.wrongType"));
            return;
        }
        if (file.size > MAX_BYTES) {
            setError(t("publicDirectory.settings.photo.tooBig"));
            return;
        }
        setError(null);
        upload.mutate(file, {
            onError: () => setError(t("publicDirectory.settings.photo.failed")),
        });
    };

    const showsImage = Boolean(photoUrl) && !unreachable;

    return (
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
            <div className="shrink-0">
                {showsImage ? (
                    <img
                        src={photoUrl as string}
                        alt={t("publicDirectory.settings.photo.current")}
                        onError={() => setUnreachable(true)}
                        className="h-28 w-28 rounded-full border border-border-subtle object-cover"
                    />
                ) : (
                    <span
                        aria-hidden="true"
                        className="grid h-28 w-28 place-items-center rounded-full border border-dashed border-border-strong bg-surface-sunken text-content-tertiary"
                    >
                        <svg
                            width="34"
                            height="34"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.5"
                        >
                            <circle cx="12" cy="8" r="3.6" />
                            <path d="M4.5 20a7.5 7.5 0 0 1 15 0" />
                        </svg>
                    </span>
                )}
            </div>

            <div className="flex min-w-0 flex-col gap-2">
                <div className="flex flex-wrap gap-2">
                    <button
                        type="button"
                        onClick={() => inputRef.current?.click()}
                        disabled={upload.isPending}
                        className="rounded-lg border border-border-strong px-4 py-2.5 text-sm font-medium text-content-primary transition-colors duration-[--duration-fast] hover:bg-surface-sunken disabled:opacity-60 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-text"
                    >
                        {upload.isPending
                            ? t("publicDirectory.settings.photo.uploading")
                            : photoUrl
                              ? t("publicDirectory.settings.photo.replace")
                              : t("publicDirectory.settings.photo.add")}
                    </button>
                    {photoUrl && (
                        <button
                            type="button"
                            onClick={() => remove.mutate()}
                            disabled={remove.isPending}
                            className="rounded-lg px-4 py-2.5 text-sm font-medium text-status-critical transition-colors duration-[--duration-fast] hover:bg-status-critical-tint disabled:opacity-60 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-text"
                        >
                            {t("publicDirectory.settings.photo.remove")}
                        </button>
                    )}
                </div>

                {/* Stated rather than engineered around: no design short of
                    expiring URLs retracts an image someone already fetched,
                    and expiring URLs break a public page. */}
                <p className="max-w-prose text-sm text-content-secondary">
                    {t("publicDirectory.settings.photo.retentionNote")}
                </p>

                {/* Said here rather than buried in a policy page: the photo
                    leaves the directory and reaches people's inboxes, and a
                    therapist deciding whether to upload one is deciding that
                    too. */}
                <p className="max-w-prose rounded-lg bg-surface-sunken px-3 py-2 text-sm text-content-secondary">
                    {t("publicDirectory.settings.photo.emailNote")}
                </p>

                {unreachable && photoUrl && (
                    <p
                        role="alert"
                        className="max-w-prose text-sm text-status-warning-on-tint"
                    >
                        {t("publicDirectory.settings.photo.unreachable")}
                    </p>
                )}
                {error && (
                    <p role="alert" className="text-sm text-status-critical">
                        {error}
                    </p>
                )}
            </div>

            <input
                ref={inputRef}
                type="file"
                accept={ACCEPTED.join(",")}
                onChange={pick}
                className="sr-only"
                aria-label={t("publicDirectory.settings.photo.add")}
            />
        </div>
    );
}
